import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Calendar,
  ClipboardList,
  FileText,
  Info,
  Loader2,
  PanelLeft,
  Wrench,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { JournalAttachments } from '@/components/journal/JournalAttachments';
import { JournalAutosaveStatus } from '@/components/journal/JournalAutosaveStatus';
import { JournalCorrectionPanel } from '@/components/journal/JournalCorrectionPanel';
import { JournalDraftFinalizeActions } from '@/components/journal/JournalDraftFinalizeActions';
import { JournalFormattedView } from '@/components/journal/JournalFormattedView';
import { JournalRichTextEditor } from '@/components/journal/JournalRichTextEditor';
import { JournalTemplatePicker } from '@/components/journal/JournalTemplatePicker';
import { JournalWriteHistory } from '@/components/journal/JournalWriteHistory';
import { JournalWriteReadingPanel } from '@/components/journal/JournalWriteReadingPanel';
import { JournalWriteTools } from '@/components/journal/JournalWriteTools';
import { CollapsedToolRail } from '@/components/journal/ToolsColumn';
import { ManageJournalTemplatesModal } from '@/components/journal/ManageJournalTemplatesModal';
import { AppointmentModal } from '@/pages/appointments/AppointmentModal';
import { useAppointment } from '@/hooks/useAppointments';
import { useAuth } from '@/lib/auth';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, type PractitionerRoleRef } from '@/lib/practitionerRole';
import {
  JOURNAL_WRITE_ALL_EMPLOYEES,
  journalWritePractitionerChoices,
} from '@/lib/journalWritePractitionerFilter';
import { filterJournalWriteHistoryEntries } from '@/lib/journalWriteHistoryList';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { useJournalTemplates } from '@/hooks/useJournalTemplates';
import {
  useAcquireJournalLock,
  useCreateJournalEntry,
  useDiscardJournalDraft,
  useFinalizeJournalEntry,
  useJournalEntries,
  useReleaseJournalLock,
  useUpdateJournalEntry,
  type JournalEntryWithAuthor,
} from '@/hooks/useJournal';
import { useJournalFinishConfirmation } from '@/hooks/useJournalFinishConfirmation';
import { useJournalWritingDraft } from '@/hooks/useJournalWritingDraft';
import { usePatientAppointments } from '@/hooks/usePatientAppointments';
import {
  useTreatmentCourseStaffView,
  useTreatmentCoursesPatientList,
} from '@/hooks/useTreatmentCourses';
import { useUploadDocument } from '@/hooks/useDocuments';
import {
  useAttachJournalEntryDocument,
  validateJournalAttachmentFile,
} from '@/hooks/useJournalAttachments';
import type { PatientRow } from '@/hooks/usePatients';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { courseStepTitle } from '@/lib/courseUiHelpers';
import { patientCaveText } from '@/lib/patientCave';
import {
  appointmentDurationMinutes,
  courseAppointmentIdSet,
  journalWriteClosePath,
  parseJournalWriteSearch,
  patientInitials,
  type JournalWriteHistoryScope,
} from '@/lib/journalWritePath';
import {
  JOURNAL_HISTORY_WIDTH_DEFAULT,
  clampJournalHistoryWidth,
  defaultJournalWriteColumns,
  journalHistoryDefaultOpen,
  maxJournalHistoryWidth,
  readJournalHistoryWidth,
  readJournalWriteColumnPrefs,
  writeJournalHistoryWidth,
  writeJournalWriteColumnPrefs,
  type JournalWriteColumnPrefs,
} from '@/lib/journalWritePrefs';
import { journalHtmlForEditor, storedJournalHtml } from '@/lib/journal/journalRichText';
import { cn } from '@/utils/cn';
import { clinicCalendarDate } from '@/utils/patientJournalPath';
import {
  canFinalizeJournalContent,
  JournalFinalizeAfterSaveError,
  persistJournalWithIntent,
  planStepJournalPersist,
  type JournalPersistIntent,
} from '@/utils/journalPersist';
const ICON = 1.75;

function useMinWidth(px: number): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(`(min-width: ${px}px)`).matches,
  );
  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${px}px)`);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [px]);
  return matches;
}

export function JournalWriteView({
  clinicId,
  patient,
  currentUserId,
  ageYears,
}: {
  clinicId: string;
  patient: PatientRow;
  currentUserId: string | null | undefined;
  ageYears: number | null;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission, isClinicAdmin, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(
    () => parseJournalWriteSearch(new URLSearchParams(location.search)),
    [location.search],
  );
  const isDesktop = useIsDesktopNav();
  const isWide = useMinWidth(1280);
  const historyPrefs = useRef(readJournalWriteColumnPrefs(currentUserId));
  const historyPrefsUser = useRef(currentUserId);
  const stored = defaultJournalWriteColumns(params.source, currentUserId);
  const [leftOpen, setLeftOpen] = useState(stored.leftOpen);
  const [rightOpen, setRightOpen] = useState(stored.rightOpen);
  const [mobileSheet, setMobileSheet] = useState<'notes' | 'tools' | null>(null);
  const [historyScope, setHistoryScope] = useState<JournalWriteHistoryScope>(
    params.history ?? (params.courseId || params.stepId ? 'course' : 'all'),
  );
  const [historySearch, setHistorySearch] = useState('');
  const [practitionerId, setPractitionerId] = useState(JOURNAL_WRITE_ALL_EMPLOYEES);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [persistIntent, setPersistIntent] = useState<JournalPersistIntent | null>(null);
  const [manageTemplates, setManageTemplates] = useState(false);
  const [quoteInsert, setQuoteInsert] = useState<{ html: string; key: number } | null>(null);
  const [readingEntry, setReadingEntry] = useState<JournalEntryWithAuthor | null>(null);
  const [historyWidth, setHistoryWidth] = useState(() => readJournalHistoryWidth(currentUserId));
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? JOURNAL_HISTORY_WIDTH_DEFAULT * 2 : window.innerWidth,
  );
  const [booking, setBooking] = useState<{
    mode: 'create' | 'edit';
    appointmentId?: string;
  } | null>(null);
  const hydratedFor = useRef<string | null>(null);
  const baselineTitle = useRef('');
  const leftDefaultApplied = useRef(false);

  const canCreate = hasPermission(clinicId, 'journal', 'create');
  const canFinalize = hasPermission(clinicId, 'journal', 'finalize');
  const canAmend = hasPermission(clinicId, 'journal', 'amend');
  const canEditCourses = hasPermission(clinicId, 'treatment_courses', 'update');

  const entriesQuery = useJournalEntries(clinicId, { patientId: patient.id });
  const appointmentsQuery = usePatientAppointments(patient.id);
  const coursesQuery = useTreatmentCoursesPatientList(clinicId, patient.id);
  const members = useClinicMembers(clinicId);
  const roles = useClinicRoles(clinicId);
  const appointmentQuery = useAppointment(params.appointmentId ?? null);
  const templatesQuery = useJournalTemplates(clinicId, patient.id);
  const finishConfirm = useJournalFinishConfirmation({
    clinicId,
    patientId: patient.id,
    patientName: `${patient.first_name} ${patient.last_name}`.trim(),
  });
  const createEntry = useCreateJournalEntry(clinicId);
  const updateEntry = useUpdateJournalEntry(clinicId);
  const finalizeEntry = useFinalizeJournalEntry(clinicId);
  const discardDraft = useDiscardJournalDraft(clinicId);
  const acquireLock = useAcquireJournalLock(clinicId);
  const releaseLock = useReleaseJournalLock(clinicId);
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachJournalEntryDocument(clinicId);

  const resolvedCourseId =
    params.courseId ??
    coursesQuery.data?.find((course) =>
      course.steps.some(
        (step) => step.id === params.stepId || step.appointment_id === params.appointmentId,
      ),
    )?.id ??
    null;
  const staffView = useTreatmentCourseStaffView(clinicId, resolvedCourseId);
  const course = staffView.data ?? null;
  const step =
    course?.steps.find((row) => row.id === params.stepId) ??
    course?.steps.find((row) => row.appointment_id === params.appointmentId) ??
    null;

  const entries = entriesQuery.data ?? [];
  const entriesReady = entriesQuery.data !== undefined;
  const entriesFailed = Boolean(entriesQuery.isError && !entriesReady);
  const entriesLoading = !entriesReady && !entriesFailed;
  const focused = params.entryId
    ? (entries.find((entry) => entry.id === params.entryId) ?? null)
    : null;
  const appointmentNotes = params.appointmentId
    ? entries.filter(
        (entry) => entry.appointment_id === params.appointmentId && !entry.discarded_at,
      )
    : [];
  const persistPlan = params.appointmentId
    ? planStepJournalPersist(appointmentNotes, params.appointmentId)
    : focused
      ? focused.finalized_at
        ? { kind: 'finalized' as const, entryId: focused.id }
        : { kind: 'update' as const, entryId: focused.id, rowVersion: focused.row_version }
      : { kind: 'create' as const };

  const activeEntryId = persistPlan.kind === 'create' ? null : persistPlan.entryId;
  const activeEntry = activeEntryId
    ? (entries.find((entry) => entry.id === activeEntryId) ?? focused)
    : focused;
  const isFinalized = persistPlan.kind === 'finalized' || Boolean(activeEntry?.finalized_at);

  const draftScope = activeEntryId
    ? { kind: 'entry' as const, entryId: activeEntryId }
    : params.appointmentId
      ? { kind: 'appointment' as const, appointmentId: params.appointmentId }
      : { kind: 'new' as const };

  const {
    status: draftStatus,
    lastSavedAt,
    restored,
    dismissRestored,
    deleteDraft,
  } = useJournalWritingDraft({
    clinicId,
    patientId: patient.id,
    userId: currentUserId,
    scope: draftScope,
    title,
    contentMd: content,
    contentHtml,
    enabled: entriesReady && !isFinalized,
    restoreOnMount: entriesReady && !isFinalized,
    baselineTitle: baselineTitle.current,
  });

  useEffect(() => {
    if (historyPrefsUser.current === currentUserId) return;
    historyPrefsUser.current = currentUserId;
    historyPrefs.current = readJournalWriteColumnPrefs(currentUserId);
    const columns = defaultJournalWriteColumns(params.source, currentUserId);
    setLeftOpen(columns.leftOpen);
    setRightOpen(columns.rightOpen);
    leftDefaultApplied.current = false;
  }, [currentUserId, params.source]);

  useEffect(() => {
    setHistoryWidth(readJournalHistoryWidth(currentUserId));
  }, [currentUserId]);

  useEffect(() => {
    const syncViewport = () => {
      setViewportWidth(window.innerWidth);
      setHistoryWidth((width) => clampJournalHistoryWidth(width, window.innerWidth));
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  function applyHistoryWidth(next: number) {
    const clamped = clampJournalHistoryWidth(next);
    setHistoryWidth(clamped);
    writeJournalHistoryWidth(currentUserId, clamped);
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector('.page-scroll')?.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const state = location.state as { citeHtml?: unknown } | null;
    if (typeof state?.citeHtml !== 'string' || !state.citeHtml.trim()) return;
    setQuoteInsert({ html: state.citeHtml, key: Date.now() });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  const earlierNoteCount = entries.filter(
    (entry) => !entry.discarded_at && entry.id !== activeEntryId,
  ).length;

  useEffect(() => {
    if (!entriesReady || leftDefaultApplied.current) return;
    leftDefaultApplied.current = true;
    // A remembered collapse or expand wins over the journal/step default,
    // for every patient and for both a new note and an existing draft.
    const open = journalHistoryDefaultOpen(params.source, earlierNoteCount, historyPrefs.current);
    if (open === true) setLeftOpen(true);
  }, [currentUserId, earlierNoteCount, entriesReady, params.source]);

  useEffect(() => {
    if (restored && !isFinalized) {
      const restoreHasBody = Boolean((restored.content_md ?? '').trim() || restored.content_html);
      if (restoreHasBody) {
        setTitle(restored.title ?? '');
        setContent(restored.content_md);
        setContentHtml(restored.content_html);
      } else if (restored.title) {
        setTitle((current) => current || restored.title || '');
      }
      dismissRestored();
    }
  }, [dismissRestored, isFinalized, restored]);

  const defaultTitle = step ? courseStepTitle(step, t('treatment_courses.step_no_service')) : '';

  if (
    entriesReady &&
    !isFinalized &&
    persistPlan.kind === 'update' &&
    hydratedFor.current !== persistPlan.entryId
  ) {
    const draft = entries.find((entry) => entry.id === persistPlan.entryId);
    if (draft) {
      hydratedFor.current = draft.id;
      const nextTitle = draft.title || defaultTitle;
      baselineTitle.current = nextTitle;
      setTitle(nextTitle);
      setContent(draft.content_md ?? '');
      setContentHtml(draft.content_html);
    }
  }

  useEffect(() => {
    if (!entriesReady || isFinalized) return;
    if (persistPlan.kind === 'update') {
      const draft = entries.find((entry) => entry.id === persistPlan.entryId);
      if (!draft || hydratedFor.current === draft.id) return;
      hydratedFor.current = draft.id;
      const nextTitle = draft.title || defaultTitle;
      baselineTitle.current = nextTitle;
      setTitle(nextTitle);
      setContent(draft.content_md ?? '');
      setContentHtml(draft.content_html);
      return;
    }
    if (persistPlan.kind === 'create' && !title && defaultTitle && hydratedFor.current !== 'new') {
      hydratedFor.current = 'new';
      baselineTitle.current = defaultTitle;
      setTitle(defaultTitle);
    }
  }, [defaultTitle, entries, entriesReady, isFinalized, persistPlan, title]);

  const leftExpanded = leftOpen && !(isDesktop && !isWide && rightOpen);
  const rightExpanded = rightOpen;
  const editorNarrow = leftExpanded && rightExpanded;

  const appointmentById = useMemo(() => {
    const map = new Map<
      string,
      { id: string; starts_at: string; service_name: string | null; clinician_name: string | null }
    >();
    for (const row of appointmentsQuery.data ?? []) {
      map.set(row.id, {
        id: row.id,
        starts_at: row.starts_at,
        service_name: row.service_name,
        clinician_name: row.clinician_name,
      });
    }
    return map;
  }, [appointmentsQuery.data]);

  const practitionerChoices = useMemo(
    () =>
      journalWritePractitionerChoices({
        members: (members.data ?? []).map((member) => ({
          user_id: member.user_id,
          name: member.name,
          role: member.role,
          custom_role_id: member.custom_role_id,
        })),
        roles: (roles.data ?? []) as PractitionerRoleRef[],
        rolesReady: roles.isSuccess,
        entries,
        selectedId: practitionerId,
      }),
    [entries, members.data, practitionerId, roles.data, roles.isSuccess],
  );
  const practitionerMemberIds = useMemo(() => {
    if (!roles.isSuccess) return (members.data ?? []).map((member) => member.user_id);
    return filterPractitionerMembers(members.data ?? [], roles.data ?? []).map(
      (member) => member.user_id,
    );
  }, [members.data, roles.data, roles.isSuccess]);
  const activeMemberIds = useMemo(
    () => (roles.isSuccess ? (members.data ?? []).map((member) => member.user_id) : []),
    [members.data, roles.isSuccess],
  );
  const historyVisibleCount = useMemo(
    () =>
      filterJournalWriteHistoryEntries(entries, {
        scope: historyScope,
        courseAppointmentIds: courseAppointmentIdSet(course?.steps ?? []),
        practitionerId,
        practitionerIds: new Set(practitionerMemberIds),
        activeMemberIds: new Set(activeMemberIds),
        search: historySearch,
        appointmentById,
      }).length,
    [
      activeMemberIds,
      appointmentById,
      course?.steps,
      entries,
      historyScope,
      historySearch,
      practitionerId,
      practitionerMemberIds,
    ],
  );

  const linkedAppointment = appointmentQuery.data ?? null;
  const currentStarts = linkedAppointment?.starts_at ?? null;
  const nextAppointment = useMemo(() => {
    const now = currentStarts ? new Date(currentStarts).getTime() : Date.now();
    const upcoming = (appointmentsQuery.data ?? [])
      .filter(
        (row) =>
          row.status !== 'cancelled' &&
          row.status !== 'no_show' &&
          new Date(row.starts_at).getTime() > now,
      )
      .sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at));
    const next = upcoming[0];
    if (!next) return null;
    const nextStep = (coursesQuery.data ?? [])
      .flatMap((row) =>
        row.steps.map((item) => ({
          ...item,
          courseName: row.template_name,
          total: row.steps.length,
        })),
      )
      .find((item) => item.appointment_id === next.id);
    return {
      id: next.id,
      starts_at: next.starts_at,
      service_name: next.service_name,
      clinician_name: next.clinician_name,
      stepLabel: nextStep
        ? t('journal.write_band_course', {
            name: nextStep.courseName,
            n: nextStep.sort_order,
            m: nextStep.total,
          })
        : null,
    };
  }, [appointmentsQuery.data, coursesQuery.data, currentStarts, t]);

  const cave = patientCaveText(patient);
  const closePath = journalWriteClosePath(patient.id, {
    ...params,
    courseId: resolvedCourseId ?? params.courseId,
    stepId: step?.id ?? params.stepId,
  });

  function close() {
    navigate(closePath);
  }

  function applyTemplate(id: string) {
    setTemplateId(id);
    if (!id) return;
    const tmpl = (templatesQuery.data ?? []).find((item) => item.id === id);
    if (!tmpl) return;
    setTitle(tmpl.title);
    const body = tmpl.content_md ?? '';
    const editorHasText = Boolean(content.trim() || contentHtml);
    setContent(body);
    // Existing text: keep the previous overwrite (markdown + html null).
    // Empty editor: pass real HTML so the editor sync cannot treat null html
    // as an echo of the last empty emit and skip the body.
    setContentHtml(editorHasText ? null : storedJournalHtml(journalHtmlForEditor(null, body)));
  }

  async function persistPending(entryId: string, files: File[]) {
    for (const file of files) {
      const validationError = validateJournalAttachmentFile(file);
      if (validationError) {
        toast.error(t(`journal.attachments.errors.${validationError}`));
        continue;
      }
      try {
        const doc = await uploadDocument.mutateAsync({
          clinicId,
          patientId: patient.id,
          file,
          tags: ['journal'],
        });
        await attachDocument.mutateAsync({
          journal_entry_id: entryId,
          document_id: doc.id,
        });
      } catch {
        toast.error(t('journal.attachments.errors.upload_failed'));
      }
    }
  }

  async function handlePersist(intent: JournalPersistIntent) {
    if (!entriesReady || entriesFailed || isFinalized) return;
    if (intent === 'finalize' && !(await finishConfirm.confirmFinish())) return;
    setPersistIntent(intent);
    try {
      switch (persistPlan.kind) {
        case 'create': {
          await persistJournalWithIntent(
            intent,
            async () => {
              const created = await createEntry.mutateAsync({
                patient_id: patient.id,
                title: title || undefined,
                content_md: content || undefined,
                content_html: contentHtml,
                template_id: templateId || undefined,
                appointment_id: params.appointmentId || undefined,
                entry_date:
                  params.appointmentId && linkedAppointment
                    ? clinicCalendarDate(linkedAppointment.starts_at)
                    : undefined,
              });
              if (pendingFiles.length > 0) {
                await persistPending(created.id, pendingFiles);
                setPendingFiles([]);
              }
              return created;
            },
            (entry) =>
              finalizeEntry.mutateAsync({
                id: entry.id,
                row_version: entry.row_version,
              }),
          );
          break;
        }
        case 'update': {
          const lock = await acquireLock.mutateAsync(persistPlan.entryId);
          if (!lock.acquired) {
            toast.error(
              t('journal.lock_held_by', {
                name: lock.holder_user_name || t('journal.editing'),
              }),
            );
            return;
          }
          try {
            await persistJournalWithIntent(
              intent,
              async () => {
                const updated = await updateEntry.mutateAsync({
                  id: persistPlan.entryId,
                  title: title || undefined,
                  content_md: content,
                  content_html: contentHtml,
                  row_version: persistPlan.rowVersion,
                });
                if (pendingFiles.length > 0) {
                  await persistPending(persistPlan.entryId, pendingFiles);
                  setPendingFiles([]);
                }
                return updated ?? { id: persistPlan.entryId, row_version: persistPlan.rowVersion };
              },
              (entry) =>
                finalizeEntry.mutateAsync({
                  id: entry.id,
                  row_version: entry.row_version,
                }),
            );
          } finally {
            if (intent === 'draft') {
              await releaseLock.mutateAsync(persistPlan.entryId).catch(() => undefined);
            }
          }
          break;
        }
        default: {
          const unexpected: never = persistPlan;
          return unexpected;
        }
      }
      await deleteDraft();
      toast.success(
        intent === 'finalize' ? t('journal.finalized_toast') : t('journal.draft_saved'),
      );
    } catch (error) {
      if (error instanceof JournalFinalizeAfterSaveError) {
        toast.error(t('journal.finalize_failed'));
        await deleteDraft();
      } else {
        toast.error(error instanceof Error ? error.message : t('common.error'));
      }
    } finally {
      setPersistIntent(null);
    }
  }

  async function handleDiscard() {
    if (!entriesReady || entriesFailed) return;
    if (!window.confirm(t('journal.discard_confirm'))) return;
    try {
      if (persistPlan.kind === 'update') {
        await discardDraft.mutateAsync(persistPlan.entryId);
      }
      await deleteDraft();
      toast.success(t('journal.draft_discarded'));
      close();
    } catch {
      toast.error(t('common.error'));
    }
  }

  function persistColumnPrefs(next: JournalWriteColumnPrefs) {
    historyPrefs.current = next;
    writeJournalWriteColumnPrefs(currentUserId, next);
  }

  function setHistoryOpen(open: boolean) {
    persistColumnPrefs({
      leftOpen: open,
      rightOpen,
      historyOpenChosen: true,
    });
    setLeftOpen(open);
  }

  function setToolsOpen(open: boolean) {
    persistColumnPrefs({
      leftOpen: historyPrefs.current.leftOpen,
      rightOpen: open,
      historyOpenChosen: historyPrefs.current.historyOpenChosen,
    });
    setRightOpen(open);
  }

  const history = (
    <JournalWriteHistory
      clinicId={clinicId}
      patientId={patient.id}
      entries={entries}
      appointmentById={appointmentById}
      courseAppointmentIds={courseAppointmentIdSet(course?.steps ?? [])}
      courseName={course?.template_name ?? null}
      scope={historyScope}
      onScopeChange={setHistoryScope}
      practitionerId={practitionerId}
      onPractitionerChange={setPractitionerId}
      practitioners={practitionerChoices}
      practitionerMemberIds={practitionerMemberIds}
      activeMemberIds={activeMemberIds}
      search={historySearch}
      onSearchChange={setHistorySearch}
      userId={currentUserId}
      onCite={(html) => setQuoteInsert({ html, key: Date.now() })}
      onCollapse={() => setHistoryOpen(false)}
      onReadFull={(entry) => {
        setMobileSheet(null);
        setReadingEntry(entry);
      }}
      resizable={isDesktop}
      width={historyWidth}
      onWidthChange={applyHistoryWidth}
      onWidthReset={() => applyHistoryWidth(JOURNAL_HISTORY_WIDTH_DEFAULT)}
      widthMax={maxJournalHistoryWidth(viewportWidth)}
    />
  );

  const tools = (
    <JournalWriteTools
      clinicId={clinicId}
      patientId={patient.id}
      patientName={`${patient.first_name} ${patient.last_name}`.trim()}
      currentUserId={currentUserId}
      canEdit={canEditCourses || canCreate}
      course={course}
      step={step}
      serviceId={params.appointmentId ? (linkedAppointment?.service_id ?? null) : null}
      serviceName={linkedAppointment?.service_name ?? null}
      servicePending={Boolean(params.appointmentId) && appointmentQuery.isPending}
      nextAppointment={nextAppointment}
      onMoveNext={() =>
        nextAppointment
          ? setBooking({ mode: 'edit', appointmentId: nextAppointment.id })
          : undefined
      }
      onNewAppointment={() => setBooking({ mode: 'create' })}
      onCollapse={() => setToolsOpen(false)}
    />
  );

  const duration =
    linkedAppointment &&
    appointmentDurationMinutes(linkedAppointment.starts_at, linkedAppointment.ends_at);

  return (
    <div className="jw">
      <header className="jw-band">
        <span className="jw-avatar" aria-hidden>
          {patientInitials(patient.first_name, patient.last_name)}
        </span>
        <div className="jw-id">
          <div className="jw-r1">
            <h1>{`${patient.first_name} ${patient.last_name}`.trim()}</h1>
            {ageYears != null ? (
              <span className="jw-age">{t('patients.header.age_years', { count: ageYears })}</span>
            ) : null}
            {cave ? (
              <span className="jw-cave">{t('journal.cave_chip', { text: cave })}</span>
            ) : null}
          </div>
          <div className="jw-r2">
            {course && step ? (
              <span className="jw-ctx">
                <ClipboardList className="h-3.5 w-3.5" strokeWidth={ICON} aria-hidden />
                {t('journal.write_band_course', {
                  name: course.template_name,
                  n: step.sort_order,
                  m: course.steps.length,
                })}
              </span>
            ) : null}
            {course && step && linkedAppointment ? <span className="jw-sep">·</span> : null}
            {linkedAppointment ? (
              <span className="jw-ctx">
                <Calendar className="h-3.5 w-3.5" strokeWidth={ICON} aria-hidden />
                {t('journal.write_band_appointment', {
                  when: formatCourseDateTime(linkedAppointment.starts_at, i18n.language),
                  detail: [
                    linkedAppointment.service_name,
                    duration
                      ? t('treatment_courses.duration_minutes', { minutes: duration })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                })}
              </span>
            ) : null}
          </div>
        </div>
        <div className="jw-act">
          <Button type="button" variant="tertiary" onClick={close}>
            <X className="h-4 w-4" strokeWidth={ICON} aria-hidden />
            {t('journal.write_close')}
          </Button>
        </div>
      </header>

      <div
        className={cn(
          'jw-grid',
          leftExpanded && 'lo',
          rightExpanded && 'ro',
          editorNarrow && 'narrow',
        )}
        style={leftExpanded ? ({ '--jw-L': `${historyWidth}px` } as CSSProperties) : undefined}
      >
        {isDesktop && !leftExpanded ? (
          <CollapsedToolRail
            label={t('journal.previous_notes')}
            icon={<FileText className="h-4 w-4" strokeWidth={ICON} aria-hidden />}
            onClick={() => setHistoryOpen(true)}
            badge={
              <span className="jw-n" data-testid="journal-history-rail-count">
                {historyVisibleCount}
              </span>
            }
          />
        ) : isDesktop ? (
          history
        ) : null}

        <div className={cn('jw-ed', editorNarrow && 'narrow')}>
          <div className="jw-mtool">
            <Button type="button" variant="secondary" onClick={() => setMobileSheet('notes')}>
              <PanelLeft className="h-4 w-4" strokeWidth={ICON} aria-hidden />
              {t('journal.mobile_notes')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setMobileSheet('tools')}>
              <Wrench className="h-4 w-4" strokeWidth={ICON} aria-hidden />
              {t('journal.mobile_tools')}
            </Button>
          </div>
          <div className="card">
            <div className="jw-scroll jw-scroller">
              {entriesLoading ? (
                <div className="jw-state" role="status" aria-live="polite">
                  <Loader2 className="h-6 w-6 animate-spin" strokeWidth={ICON} aria-hidden />
                  {t('app.loading')}
                </div>
              ) : entriesFailed ? (
                <div className="jw-state" role="alert">
                  <p>{t('journal.load_error')}</p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void entriesQuery.refetch()}
                  >
                    {t('app.retry')}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="jw-ed-h">
                    <div>
                      <h2>
                        {isFinalized
                          ? activeEntry?.title || t('journal.untitled')
                          : t('journal.write_heading')}
                      </h2>
                      {!isFinalized ? (
                        <p className="mt-1 text-sm text-ds-muted">
                          <span className="text-sm text-ds-muted">
                            {t('journal.filter_status_draft')}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div className="text-right">
                      {!isFinalized ? (
                        <JournalAutosaveStatus status={draftStatus} lastSavedAt={lastSavedAt} />
                      ) : null}
                      {!isFinalized ? (
                        <p className="text-xs text-ds-muted">{t('journal.you_have_lock')}</p>
                      ) : null}
                    </div>
                  </div>

                  {isFinalized && activeEntry ? (
                    <>
                      <div className="jw-ok">
                        <Info className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                        {t('journal.finalize_locked')}
                      </div>
                      {activeEntry.template_title_snapshot ? (
                        <p className="mt-4 text-sm text-ds-muted">
                          {t('journal.template')}: {activeEntry.template_title_snapshot}
                          {activeEntry.finalized_at
                            ? ` · ${t('journal.finalized_by', {
                                date: formatCourseDateTime(activeEntry.finalized_at, 'da'),
                                name: activeEntry.author_name ?? '',
                              })}`
                            : null}
                        </p>
                      ) : null}
                      <div className="mt-4">
                        <JournalFormattedView
                          html={activeEntry.content_html}
                          text={activeEntry.content_md}
                        />
                      </div>
                      <div className="mt-4">
                        <JournalAttachments
                          clinicId={clinicId}
                          patientId={patient.id}
                          entryId={activeEntry.id}
                          canEdit={false}
                        />
                      </div>
                      {canAmend ? (
                        <JournalCorrectionPanel
                          clinicId={clinicId}
                          patientId={patient.id}
                          entryId={activeEntry.id}
                          canAmend={canAmend}
                          startToken={params.amend ? 1 : 0}
                          variant="write"
                        />
                      ) : (
                        <div className="jw-foot">
                          <p className="hint">
                            <Info className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                            {t('journal.finalize_locked')}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="jw-ed-fields">
                        <JournalTemplatePicker
                          templates={templatesQuery.data ?? []}
                          value={templateId}
                          onChange={applyTemplate}
                          onManage={() => setManageTemplates(true)}
                        />
                        <Input
                          label={t('journal.title')}
                          value={title}
                          onChange={(event) => setTitle(event.target.value)}
                          placeholder={t('journal.title_placeholder')}
                        />
                      </div>
                      <div className="mt-4">
                        <JournalRichTextEditor
                          clinicId={clinicId}
                          patientId={patient.id}
                          html={contentHtml}
                          text={content}
                          onChange={(value) => {
                            setContent(value.text);
                            setContentHtml(value.html);
                          }}
                          placeholder={t('journal.phrase_hint')}
                          insertHtmlRequest={quoteInsert}
                        />
                      </div>
                      <div className="mt-4">
                        <JournalAttachments
                          clinicId={clinicId}
                          patientId={patient.id}
                          entryId={persistPlan.kind === 'update' ? persistPlan.entryId : null}
                          pendingFiles={pendingFiles}
                          onPendingFilesChange={setPendingFiles}
                          canEdit
                        />
                      </div>
                      <div className="jw-foot">
                        <p className="hint">
                          <Info className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                          {t('journal.finalize_hint')}
                        </p>
                        <div className="jw-foot-btns">
                          <Button
                            type="button"
                            variant="tertiary"
                            onClick={() => void handleDiscard()}
                            disabled={persistIntent != null}
                          >
                            {t('journal.discard_draft')}
                          </Button>
                          <JournalDraftFinalizeActions
                            busy={persistIntent != null || finishConfirm.pending}
                            pendingIntent={persistIntent ?? (finishConfirm.pending ? 'finalize' : null)}
                            canFinalize={canFinalize && canFinalizeJournalContent(content)}
                            onSaveDraft={() => void handlePersist('draft')}
                            onFinalize={() => void handlePersist('finalize')}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {isDesktop && !rightExpanded ? (
          <CollapsedToolRail
            label={t('journal.tools')}
            icon={<Wrench className="h-4 w-4" strokeWidth={ICON} aria-hidden />}
            onClick={() => setToolsOpen(true)}
          />
        ) : isDesktop ? (
          tools
        ) : null}
      </div>

      {!isDesktop && mobileSheet === 'notes' ? (
        <div className="jw-sheet" role="dialog" aria-label={t('journal.previous_notes')}>
          <div className="jw-sheet-h">
            <h2 className="text-base font-semibold">{t('journal.previous_notes')}</h2>
            <Button type="button" variant="ghost" size="icon" onClick={() => setMobileSheet(null)}>
              <X className="h-4 w-4" strokeWidth={ICON} />
            </Button>
          </div>
          {history}
        </div>
      ) : null}
      {!isDesktop && mobileSheet === 'tools' ? (
        <div className="jw-sheet" role="dialog" aria-label={t('journal.tools')}>
          <div className="jw-sheet-h">
            <h2 className="text-base font-semibold">{t('journal.tools')}</h2>
            <Button type="button" variant="ghost" size="icon" onClick={() => setMobileSheet(null)}>
              <X className="h-4 w-4" strokeWidth={ICON} />
            </Button>
          </div>
          {tools}
        </div>
      ) : null}

      {manageTemplates ? (
        <ManageJournalTemplatesModal
          clinicId={clinicId}
          isAdmin={isPlatformAdmin || isClinicAdmin(clinicId)}
          onClose={() => setManageTemplates(false)}
        />
      ) : null}
      {booking ? (
        <AppointmentModal
          clinicId={clinicId}
          mode={booking.mode}
          appointmentId={booking.appointmentId}
          initialPatientId={patient.id}
          onClose={() => setBooking(null)}
        />
      ) : null}
      {readingEntry ? (
        <JournalWriteReadingPanel
          entry={readingEntry}
          onCite={(html) => setQuoteInsert({ html, key: Date.now() })}
          onClose={() => setReadingEntry(null)}
        />
      ) : null}
      {finishConfirm.dialog}
    </div>
  );
}
